import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiArrowLeft } from 'react-icons/fi';

const MaterialsAdmin = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/materials`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setMaterials(response.data.materials || []);
      setError(null);
    } catch (err) {
      setError('Errore nel caricamento dei materiali');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', isActive: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        setError('Il nome del materiale è obbligatorio');
        return;
      }

      const baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}`;
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      };

      if (editingId) {
        // Update
        await axios.put(`${baseUrl}/admin/materials/${editingId}`, formData, config);
      } else {
        // Create
        await axios.post(`${baseUrl}/admin/materials`, formData, config);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchMaterials();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante il salvataggio');
      console.error(err);
    }
  };

  const handleEdit = (material) => {
    setFormData({
      name: material.name,
      description: material.description || '',
      isActive: material.isActive
    });
    setEditingId(material._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo materiale?')) {
      return;
    }

    try {
      const baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}`;
      await axios.delete(`${baseUrl}/admin/materials/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchMaterials();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante l\'eliminazione');
      console.error(err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Torna alla dashboard"
          >
            <FiArrowLeft size={24} className="text-gray-600" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">🏭 Gestione Materiali</h1>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition"
          >
            <FiPlus size={20} />
            Nuovo Materiale
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          ✅ Operazione completata con successo!
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4 text-gray-800">
            {editingId ? 'Modifica Materiale' : 'Nuovo Materiale'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nome *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Es: Cartone, Plastica, Carta..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descrizione
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descrizione opzionale del materiale..."
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="text-sm font-semibold text-gray-700">
                Materiale attivo
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition"
              >
                <FiCheck size={20} />
                Salva
              </button>
              <button
                onClick={resetForm}
                className="flex items-center gap-2 bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition"
              >
                <FiX size={20} />
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-center py-8 text-gray-600">Caricamento materiali...</p>
      ) : materials.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Nessun materiale configurato</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th className="px-4 py-3 text-left text-gray-700 font-semibold">Nome</th>
                <th className="px-4 py-3 text-left text-gray-700 font-semibold">Descrizione</th>
                <th className="px-4 py-3 text-left text-gray-700 font-semibold">Stato</th>
                <th className="px-4 py-3 text-center text-gray-700 font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((material) => (
                <tr key={material._id} className="even:bg-gray-50 border-b border-gray-200">
                  <td className="px-4 py-3 font-medium text-gray-800">{material.name}</td>
                  <td className="px-4 py-3 text-gray-600">{material.description || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      material.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {material.isActive ? '✓ Attivo' : '✗ Inattivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(material)}
                        className="text-blue-600 hover:text-blue-800 transition"
                        title="Modifica"
                      >
                        <FiEdit2 size={20} />
                      </button>
                      <button
                        onClick={() => handleDelete(material._id)}
                        className="text-red-600 hover:text-red-800 transition"
                        title="Elimina"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MaterialsAdmin;
